import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useGmailComposeUrl } from "@/lib/mail";
import {
    ArrowLeft,
    Loader2,
    ClipboardList,
    CalendarPlus,
    Trash2,
    User,
    Phone,
    Mail,
    Inbox,
    ShieldCheck,
    FileText,
    X,
    CalendarCheck,
    Filter,
    SortAsc,
    Activity,
    AlertTriangle,
    RotateCcw,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useContactTracker, lastContactedLabel, isContactStale } from "@/lib/contactTracker";
import ContactConfirmBanner from "@/components/ContactConfirmBanner";
import ContactDot from "@/components/ContactDot";
import {
    isoDate,
    addDays,
    startOfWeekMon,
    formatTimeDisplay,
} from "@/lib/schedule";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PendingAssessments() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const contactTracker = useContactTracker();

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (user.role !== "owner") {
            toast.error("Only owners can manage assessment requests");
            navigate("/coach", { replace: true });
        }
    }, [loading, user, navigate]);

    const [requests, setRequests] = useState([]);
    const [busy, setBusy] = useState(true);
    const [activeReq, setActiveReq] = useState(null);
    const [viewingWaiver, setViewingWaiver] = useState(null);
    const [viewingLead, setViewingLead] = useState(null);

    // Tabs + filters + sort
    const [tab, setTab] = useState("pending"); // pending | confirmed | dropped
    const [waiverFilter, setWaiverFilter] = useState("all"); // all | signed | will-sign
    const [timeFilter, setTimeFilter] = useState("all"); // all | morning | midday | afternoon
    const [sortBy, setSortBy] = useState("newest"); // newest | oldest | waiver
    // Dropped (anti-bot quarantine) state
    const [dropped, setDropped] = useState([]);
    const [droppedCount, setDroppedCount] = useState(0);

    const refresh = async (statusOverride) => {
        const s = statusOverride || tab;
        setBusy(true);
        try {
            if (s === "dropped") {
                const r = await axios.get(`${API}/coach/assessment-requests/dropped`, {
                    params: { status: "dropped" }, withCredentials: true,
                });
                setDropped(r.data || []);
            } else {
                const r = await axios.get(`${API}/coach/assessment-requests`, {
                    params: { status: s }, withCredentials: true,
                });
                setRequests(r.data || []);
            }
        } catch (e) {
            toast.error("Couldn't load requests", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };
    // Always keep the dropped count in sync regardless of which tab is active
    // so the warning badge updates without forcing a tab switch.
    const refreshDroppedCount = async () => {
        try {
            const r = await axios.get(`${API}/coach/assessment-requests/dropped`, {
                params: { status: "dropped" }, withCredentials: true,
            });
            setDroppedCount((r.data || []).length);
        } catch { /* non-fatal */ }
    };
    useEffect(() => {
        if (user?.role === "owner") { refresh(); refreshDroppedCount(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, tab]);

    // Reload after any contact log so the "Last emailed" pill shows up.
    useEffect(() => {
        if (contactTracker.lastConfirmed) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactTracker.lastConfirmed]);

    const dismiss = async (request_id) => {
        if (!window.confirm("Dismiss this request?")) return;
        try {
            await axios.delete(`${API}/coach/assessment-requests/${request_id}`, { withCredentials: true });
            toast.success("Dismissed");
            refresh();
        } catch (e) {
            toast.error("Couldn't dismiss", { description: e?.response?.data?.detail });
        }
    };

    const recoverDropped = async (dropped_id, displayName) => {
        if (!window.confirm(`Recover "${displayName}" as a real lead?\n\nThis creates the user + athlete rows and a pending assessment request, exactly as if the anti-bot filter hadn't blocked them.`)) return;
        try {
            const r = await axios.post(`${API}/coach/assessment-requests/dropped/${dropped_id}/recover`, {}, { withCredentials: true });
            toast.success("Recovered", { description: `New pending request: ${r.data.request_id}` });
            await refresh();
            await refreshDroppedCount();
        } catch (e) {
            toast.error("Couldn't recover", { description: e?.response?.data?.detail });
        }
    };

    const dismissDropped = async (dropped_id) => {
        if (!window.confirm("Mark this as a confirmed bot? It will be hidden from the Dropped tab but kept in the DB for audit.")) return;
        try {
            await axios.delete(`${API}/coach/assessment-requests/dropped/${dropped_id}`, { withCredentials: true });
            toast.success("Marked as bot");
            await refresh();
            await refreshDroppedCount();
        } catch (e) {
            toast.error("Couldn't dismiss", { description: e?.response?.data?.detail });
        }
    };

    // Apply client-side filtering + sorting on top of the server-side status fetch
    const visibleRequests = useMemo(() => {
        let rows = [...requests];
        if (waiverFilter !== "all") {
            rows = rows.filter((r) =>
                waiverFilter === "signed" ? !!r.sign_waiver_now : !r.sign_waiver_now,
            );
        }
        if (timeFilter !== "all") {
            const needle = timeFilter; // "morning" | "midday" | "afternoon"
            rows = rows.filter((r) => (r.preferred_time || "").toLowerCase().includes(needle));
        }
        rows.sort((a, b) => {
            if (sortBy === "oldest") return (a.created_at || "").localeCompare(b.created_at || "");
            if (sortBy === "waiver") {
                const av = a.sign_waiver_now ? 0 : 1;
                const bv = b.sign_waiver_now ? 0 : 1;
                if (av !== bv) return av - bv;
                return (b.created_at || "").localeCompare(a.created_at || "");
            }
            return (b.created_at || "").localeCompare(a.created_at || ""); // newest
        });
        return rows;
    }, [requests, waiverFilter, timeFilter, sortBy]);

    if (loading) {
        return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand-red" /></div>;
    }

    return (
        <div data-testid="pending-assessments" className="min-h-screen bg-black text-white">
            <div className="max-w-5xl mx-auto px-4 pt-28 pb-10">
                <div className="flex items-center gap-3 mb-6">
                    <Link to="/coach" className="text-zinc-200 hover:text-white inline-flex items-center gap-1">
                        <ArrowLeft className="w-4 h-4" /> Coach console
                    </Link>
                </div>
                <div className="flex items-end gap-3 mb-6">
                    <div>
                        <h1 className="text-4xl sm:text-5xl font-black tracking-tight flex items-center gap-3">
                            <ClipboardList className="w-8 h-8 text-brand-red" />
                            Assessment Requests
                        </h1>
                        <p className="mt-2 text-zinc-200">
                            Leads from the home page. Confirm a slot to schedule, dismiss to clear, or
                            review confirmed assessments under the Confirmed tab.
                        </p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-zinc-800 mb-5">
                    {[
                        { v: "pending", label: "Needs scheduling", icon: ClipboardList, badge: 0 },
                        { v: "confirmed", label: "Confirmed", icon: CalendarCheck, badge: 0 },
                        { v: "dropped", label: "Dropped (review)", icon: AlertTriangle, badge: droppedCount },
                    ].map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.v;
                        const warn = t.v === "dropped" && t.badge > 0;
                        return (
                            <button
                                key={t.v}
                                type="button"
                                data-testid={`tab-${t.v}`}
                                onClick={() => setTab(t.v)}
                                className={`px-4 py-3 text-[12px] tracking-[0.2em] uppercase transition-colors inline-flex items-center gap-2 border-b-2 -mb-px ${
                                    active
                                        ? (warn ? "border-amber-400 text-amber-300" : "border-brand-red text-white")
                                        : (warn ? "border-transparent text-amber-300 hover:text-amber-200" : "border-transparent text-zinc-400 hover:text-white")
                                }`}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                {t.label}
                                {t.badge > 0 && (
                                    <span data-testid={`tab-${t.v}-badge`} className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-[10px] font-bold rounded-full bg-amber-500 text-black">
                                        {t.badge}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Filters */}
                {tab !== "dropped" && (
                <div className="flex flex-wrap gap-3 mb-5 text-xs">
                    <div className="inline-flex items-center gap-2 border border-zinc-800 rounded-lg px-2 py-1.5">
                        <Filter className="w-3 h-3 text-zinc-400" />
                        <span className="text-zinc-400 tracking-[0.15em] uppercase text-[10px]">Time</span>
                        {[
                            { v: "all", l: "All" },
                            { v: "morning", l: "Morning" },
                            { v: "midday", l: "Midday" },
                            { v: "afternoon", l: "Eve" },
                        ].map((opt) => (
                            <button
                                key={opt.v}
                                type="button"
                                data-testid={`filter-time-${opt.v}`}
                                onClick={() => setTimeFilter(opt.v)}
                                className={`px-2 py-0.5 rounded text-[11px] transition-colors ${
                                    timeFilter === opt.v
                                        ? "bg-brand-red/20 text-brand-red"
                                        : "text-zinc-300 hover:text-white"
                                }`}
                            >{opt.l}</button>
                        ))}
                    </div>

                    {/* Waiver filter retired — waiver lives on Wufoo for now.
                        Re-enable when the in-app legal waiver is restored.
                        {tab === "pending" && ( ... )} */}

                    <div className="inline-flex items-center gap-2 border border-zinc-800 rounded-lg px-2 py-1.5 ml-auto">
                        <SortAsc className="w-3 h-3 text-zinc-400" />
                        <span className="text-zinc-400 tracking-[0.15em] uppercase text-[10px]">Sort</span>
                        <select
                            value={sortBy}
                            data-testid="filter-sort"
                            onChange={(e) => setSortBy(e.target.value)}
                            className="bg-transparent text-zinc-100 text-[11px] focus:outline-none"
                        >
                            <option value="newest">Newest first</option>
                            <option value="oldest">Oldest first</option>
                        </select>
                    </div>
                </div>
                )}

                {busy ? (
                    <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-red" /></div>
                ) : tab === "dropped" ? (
                    dropped.length === 0 ? (
                        <div className="border border-zinc-800 rounded-2xl bg-zinc-950/50 p-12 text-center">
                            <ShieldCheck className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                            <p className="text-zinc-300">No suspected bots have been blocked. ✅</p>
                            <p className="text-zinc-500 text-xs mt-2">Only honeypot-confirmed bots land here. Real submissions always go straight to <strong>Needs scheduling</strong> — flagged ones get an amber "Flagged" chip on the card.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="border border-amber-700/50 bg-amber-900/15 rounded-lg p-4 text-sm">
                                <div className="text-amber-200 font-semibold mb-1 inline-flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4" /> {dropped.length} suspected bot{dropped.length === 1 ? "" : "s"} blocked
                                </div>
                                <p className="text-amber-200/80 text-xs leading-relaxed">
                                    These tripped the honeypot — a hidden field a real human can't fill. They're held here for audit only. If one is somehow a real person, click <strong>Recover</strong> to promote it to a real lead.
                                </p>
                            </div>
                            {dropped.map((d) => (
                                <DroppedCard
                                    key={d.dropped_id}
                                    rec={d}
                                    onRecover={() => recoverDropped(d.dropped_id, `${d.parent_first_name || ""} ${d.parent_last_name || ""}`.trim() || d.email || d.dropped_id)}
                                    onDismiss={() => dismissDropped(d.dropped_id)}
                                />
                            ))}
                        </div>
                    )
                ) : visibleRequests.length === 0 ? (
                    <div className="border border-zinc-800 rounded-2xl bg-zinc-950/50 p-12 text-center">
                        <Inbox className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                        <p className="text-zinc-300">
                            {tab === "pending"
                                ? "No leads to schedule right now."
                                : "No confirmed assessments match the current filter."}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {visibleRequests.map((r) => (
                            <RequestCard
                                key={r.request_id}
                                req={r}
                                tab={tab}
                                contactTracker={contactTracker}
                                onView={() => setViewingLead(r)}
                                onConfirm={() => setActiveReq(r)}
                                onDismiss={() => dismiss(r.request_id)}
                                onViewWaiver={() => setViewingWaiver(r)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {activeReq && (
                <ConfirmSlotModal
                    req={activeReq}
                    onClose={() => setActiveReq(null)}
                    onDone={() => { setActiveReq(null); refresh(); }}
                />
            )}
            {viewingWaiver && (
                <SignedWaiverModal
                    req={viewingWaiver}
                    onClose={() => setViewingWaiver(null)}
                />
            )}
            {viewingLead && (
                <LeadDetailModal
                    req={viewingLead}
                    contactTracker={contactTracker}
                    onClose={() => setViewingLead(null)}
                    onConfirm={() => { setActiveReq(viewingLead); setViewingLead(null); }}
                    onDismiss={() => { dismiss(viewingLead.request_id); setViewingLead(null); }}
                    onViewWaiver={() => { setViewingWaiver(viewingLead); setViewingLead(null); }}
                    isConfirmedTab={tab === "confirmed"}
                />
            )}
            <ContactConfirmBanner tracker={contactTracker} />
        </div>
    );
}

function RequestCard({ req, tab, contactTracker, onView, onConfirm, onDismiss, onViewWaiver }) {
    const composeUrl = useGmailComposeUrl();
    const waiverSigned = !!req.sign_waiver_now; // legacy: kept for historical e-signed records
    const isConfirmed = tab === "confirmed" && !!req.confirmed_slot;
    const slot = req.confirmed_slot;
    const stop = (handler) => (e) => { e.stopPropagation(); handler(); };
    return (
        <div
            data-testid={`request-${req.request_id}`}
            role="button"
            tabIndex={0}
            onClick={onView}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onView(); } }}
            className="border border-zinc-800 rounded-2xl bg-zinc-950/50 p-5 cursor-pointer hover:border-brand-red transition-colors focus:outline-none focus:border-brand-red"
        >
            <div className="flex flex-wrap justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-lg font-semibold flex items-center gap-2 flex-wrap">
                        <User className="w-4 h-4 text-brand-red" />
                        {req.parent_name}
                        <ContactDot lastContactedAt={req.last_contacted_at} byName={req.last_contacted_by_name} />
                        <ContactDot lastContactedAt={req.last_contacted_at} byName={req.last_contacted_by_name} />
                        {waiverSigned ? (
                            <span
                                data-testid={`waiver-signed-badge-${req.request_id}`}
                                className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-brand-navy-200 bg-brand-navy-900/60 border border-brand-navy-700 rounded-full px-2 py-0.5"
                            >
                                <ShieldCheck className="w-3 h-3" /> Waiver signed
                            </span>
                        ) : (
                            <span
                                data-testid={`waiver-link-sent-${req.request_id}`}
                                title="Wufoo waiver link emailed — verify submission in your Wufoo dashboard"
                                className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-zinc-300 bg-zinc-900/60 border border-zinc-700 rounded-full px-2 py-0.5"
                            >
                                <FileText className="w-3 h-3" /> Waiver link sent
                            </span>
                        )}
                        {isConfirmed && (
                            <span
                                data-testid={`confirmed-badge-${req.request_id}`}
                                className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-emerald-200 bg-emerald-950/60 border border-emerald-800 rounded-full px-2 py-0.5"
                            >
                                <CalendarCheck className="w-3 h-3" /> Confirmed
                            </span>
                        )}
                        {Array.isArray(req.suspicious_signals) && req.suspicious_signals.length > 0 && (
                            <span
                                data-testid={`flagged-badge-${req.request_id}`}
                                title={`Anti-bot flagged this submission: ${req.suspicious_signals.join(", ")}. Likely still a real lead — review before reaching out.`}
                                className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-amber-200 bg-amber-950/60 border border-amber-700 rounded-full px-2 py-0.5"
                            >
                                <AlertTriangle className="w-3 h-3" /> Flagged
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-zinc-300 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        <a
                            href={composeUrl.intro(req).url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={composeUrl.title}
                            onClick={(e) => {
                                e.stopPropagation();
                                contactTracker?.prompt({
                                    type: "request",
                                    id: req.request_id,
                                    name: (req.parent_name || "").split(" ")[0] || req.email,
                                });
                            }}
                            className="inline-flex items-center gap-1 hover:text-brand-red transition-colors"
                        >
                            <Mail className="w-3 h-3" /> {req.email}
                        </a>
                        <a
                            href={`tel:${req.phone}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 hover:text-brand-red transition-colors"
                        >
                            <Phone className="w-3 h-3" /> {req.phone}
                        </a>
                        <span>· requested {new Date(req.created_at).toLocaleString()}</span>
                    </div>
                    {req.last_contacted_at && (
                        <div
                            data-testid={`req-last-contacted-${req.request_id}`}
                            className={`mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-wider px-2 py-1 border ${
                                isContactStale(req.last_contacted_at, 7)
                                    ? "border-amber-700/60 bg-amber-950/30 text-amber-200"
                                    : "border-emerald-900/60 bg-emerald-950/20 text-emerald-200"
                            }`}
                        >
                            <Mail className="w-3 h-3" />
                            Emailed by {req.last_contacted_by_name || "—"} · {lastContactedLabel(req.last_contacted_at)}
                        </div>
                    )}
                </div>
                <div className="flex gap-2 shrink-0">
                    {isConfirmed ? null : (
                        <button
                            type="button"
                            data-testid={`confirm-${req.request_id}`}
                            onClick={onConfirm}
                            className="btn-tactical text-xs"
                        >
                            <CalendarPlus className="w-3.5 h-3.5" /> Confirm & schedule
                        </button>
                    )}
                    <button
                        type="button"
                        data-testid={`dismiss-${req.request_id}`}
                        onClick={stop(onDismiss)}
                        className="text-xs text-zinc-300 hover:text-brand-red px-2"
                        title={isConfirmed ? "Archive" : "Dismiss"}
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {isConfirmed && slot && (
                <div className="mt-4 border border-emerald-700/60 bg-emerald-950/30 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`confirmed-slot-${req.request_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                        <CalendarCheck className="w-6 h-6 text-emerald-300 flex-shrink-0" />
                        <div className="min-w-0">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-emerald-300 mb-0.5">Scheduled for</div>
                            <div className="font-display text-xl tracking-wider text-white">
                                {new Date(`${slot.date}T00:00:00`).toLocaleDateString(undefined, {
                                    weekday: "long", month: "long", day: "numeric",
                                })}
                                {" · "}
                                {formatTimeDisplay(slot.start_time)}
                            </div>
                            <div className="text-[11px] text-zinc-400 mt-0.5">
                                {slot.label || `${slot.duration_minutes || 60} min assessment`}
                            </div>
                        </div>
                    </div>
                    <Link
                        to={`/coach/assessment/new?request_id=${req.request_id}`}
                        data-testid={`start-assessment-${req.request_id}`}
                        onClick={stop(() => {})}
                        className="btn-tactical text-xs whitespace-nowrap"
                    >
                        <Activity className="w-3.5 h-3.5" /> Start assessment
                    </Link>
                </div>
            )}

            <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-1.5">Athletes</div>
                    {(req.athletes || []).length === 0 ? (
                        <div className="text-sm text-zinc-300">— Adult signing themselves up</div>
                    ) : (
                        <ul className="space-y-1 text-sm">
                            {req.athletes.map((a) => (
                                <li key={a.athlete_id} className="text-zinc-200">
                                    {a.name}
                                    <span className="text-xs text-zinc-300"> · {a.dob || "no DOB"}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-1.5">
                        {isConfirmed ? "Originally preferred" : "Preferred time"}
                    </div>
                    <p className="text-sm text-zinc-300 whitespace-pre-wrap">{req.preferred_time}</p>
                    {req.goals && (
                        <>
                            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-1.5 mt-3">Main goals</div>
                            <p className="text-sm text-zinc-100 whitespace-pre-wrap">{req.goals}</p>
                        </>
                    )}
                    {req.notes && (
                        <>
                            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-1.5 mt-3">Notes</div>
                            <p className="text-sm text-zinc-200 whitespace-pre-wrap">{req.notes}</p>
                        </>
                    )}
                    {waiverSigned && req.waiver && (
                        <button
                            type="button"
                            data-testid={`view-waiver-${req.request_id}`}
                            onClick={stop(onViewWaiver)}
                            className="mt-3 inline-flex items-center gap-1.5 text-xs text-brand-navy-200 hover:text-white underline-offset-2 hover:underline transition-colors"
                        >
                            <FileText className="w-3.5 h-3.5" /> View signed waiver
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function LeadDetailModal({ req, contactTracker, onClose, onConfirm, onDismiss, onViewWaiver, isConfirmedTab }) {
    const composeUrl = useGmailComposeUrl();
    const waiverSigned = !!req.sign_waiver_now;
    const isConfirmed = isConfirmedTab && !!req.confirmed_slot;
    const slot = req.confirmed_slot;
    const athletes = req.athletes || [];
    const wantsWaiverSent = req.send_waiver_link === true;

    return (
        <div
            data-testid="lead-detail-modal"
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-2xl my-8 max-h-[calc(100vh-4rem)] flex flex-col"
            >
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red">
                            Lead{isConfirmed ? " · Confirmed" : ""}
                        </div>
                        <h3 className="font-display text-2xl tracking-wider uppercase text-white truncate">
                            {req.parent_name}
                        </h3>
                    </div>
                    <button
                        type="button"
                        data-testid="lead-detail-close"
                        onClick={onClose}
                        className="text-zinc-300 hover:text-white shrink-0"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-6 py-5 overflow-y-auto space-y-5">
                    {/* Contact */}
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                        <a
                            href={composeUrl.intro(req).url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={composeUrl.title}
                            onClick={() => contactTracker?.prompt({
                                type: "request",
                                id: req.request_id,
                                name: (req.parent_name || "").split(" ")[0] || req.email,
                            })}
                            className="inline-flex items-center gap-2 text-zinc-200 hover:text-brand-red truncate"
                        >
                            <Mail className="w-4 h-4 text-brand-red shrink-0" /> {req.email}
                        </a>
                        <a href={`tel:${req.phone}`} className="inline-flex items-center gap-2 text-zinc-200 hover:text-brand-red truncate">
                            <Phone className="w-4 h-4 text-brand-red shrink-0" /> {req.phone}
                        </a>
                    </div>

                    {req.last_contacted_at && (
                        <div
                            data-testid="modal-last-contacted"
                            className={`flex items-center gap-2 text-xs px-3 py-2 border ${
                                isContactStale(req.last_contacted_at, 7)
                                    ? "border-amber-700/60 bg-amber-950/30 text-amber-200"
                                    : "border-emerald-900/60 bg-emerald-950/20 text-emerald-200"
                            }`}
                        >
                            <Mail className="w-3.5 h-3.5" />
                            <span>Last emailed by <strong>{req.last_contacted_by_name || "—"}</strong> · {lastContactedLabel(req.last_contacted_at)}</span>
                            {isContactStale(req.last_contacted_at, 7) && (
                                <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-amber-300">Follow up</span>
                            )}
                        </div>
                    )}

                    {/* Confirmed slot block */}
                    {isConfirmed && slot && (
                        <div className="border border-emerald-700/60 bg-emerald-950/30 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <CalendarCheck className="w-6 h-6 text-emerald-300 flex-shrink-0" />
                                <div className="min-w-0">
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-emerald-300 mb-0.5">Scheduled for</div>
                                    <div className="font-display text-xl tracking-wider text-white">
                                        {new Date(`${slot.date}T00:00:00`).toLocaleDateString(undefined, {
                                            weekday: "long", month: "long", day: "numeric",
                                        })}
                                        {" · "}
                                        {formatTimeDisplay ? formatTimeDisplay(slot.start_time) : (slot.start_time || "").replace(/(:\d{2})$/, "")}
                                    </div>
                                    <div className="text-[11px] text-zinc-400 mt-0.5">
                                        {slot.label || `${slot.duration_minutes || 60} min assessment`}
                                    </div>
                                </div>
                            </div>
                            <Link
                                to={`/coach/assessment/new?request_id=${req.request_id}`}
                                data-testid={`lead-detail-start-assessment-${req.request_id}`}
                                onClick={onClose}
                                className="btn-tactical text-xs whitespace-nowrap"
                            >
                                <Activity className="w-3.5 h-3.5" /> Start assessment
                            </Link>
                        </div>
                    )}

                    {/* Athletes */}
                    <Block label="Athletes">
                        {athletes.length === 0 ? (
                            <p className="text-sm text-zinc-300">Adult signing themselves up.</p>
                        ) : (
                            <ul className="space-y-2">
                                {athletes.map((a) => (
                                    <li key={a.athlete_id} className="text-sm border border-zinc-900 rounded-lg p-3 bg-zinc-900/40">
                                        <div className="text-white font-medium">{a.name}</div>
                                        <div className="text-[11px] text-zinc-300 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                                            {a.dob && <span>DOB: {a.dob}</span>}
                                            {a.grade && <span>Grade: {a.grade}</span>}
                                            {a.gender && <span>{a.gender}</span>}
                                            {a.sports?.length > 0 && <span>{a.sports.join(", ")}</span>}
                                        </div>
                                        {a.goals && <p className="mt-1.5 text-[12px] text-zinc-200 whitespace-pre-wrap">{a.goals}</p>}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Block>

                    {/* Preferred time */}
                    <Block label={isConfirmed ? "Originally preferred time" : "Preferred time"}>
                        <p className="text-sm text-zinc-200 whitespace-pre-wrap">{req.preferred_time || "—"}</p>
                    </Block>

                    {/* Goals */}
                    {req.goals && (
                        <Block label="Main goals">
                            <p className="text-sm text-zinc-100 whitespace-pre-wrap">{req.goals}</p>
                        </Block>
                    )}

                    {/* Notes */}
                    {req.notes && (
                        <Block label="Notes from lead">
                            <p className="text-sm text-zinc-200 whitespace-pre-wrap">{req.notes}</p>
                        </Block>
                    )}

                    {/* Waiver status */}
                    <Block label="Waiver">
                        {waiverSigned ? (
                            <button
                                type="button"
                                onClick={onViewWaiver}
                                className="inline-flex items-center gap-1.5 text-xs text-brand-navy-200 hover:text-white underline-offset-2 hover:underline transition-colors"
                            >
                                <ShieldCheck className="w-3.5 h-3.5" /> Signed — view
                            </button>
                        ) : (
                            <div className="text-xs text-zinc-300 inline-flex items-center gap-1.5">
                                <FileText className="w-3.5 h-3.5 text-zinc-400" />
                                {wantsWaiverSent ? "Wufoo waiver link emailed — verify in your Wufoo dashboard" : "Not yet sent"}
                            </div>
                        )}
                    </Block>

                    {/* Meta */}
                    <div className="grid sm:grid-cols-2 gap-3 text-[11px] text-zinc-400 pt-2 border-t border-zinc-900">
                        <div><span className="text-zinc-500">Requested:</span> {new Date(req.created_at).toLocaleString()}</div>
                        {req.request_id && <div className="truncate" title={req.request_id}><span className="text-zinc-500">Ref:</span> {req.request_id}</div>}
                    </div>
                </div>

                {/* Footer actions */}
                <div className="px-6 py-4 border-t border-zinc-800 flex flex-wrap justify-end gap-2">
                    <button
                        type="button"
                        data-testid="lead-detail-dismiss"
                        onClick={onDismiss}
                        className="btn-ghost-tactical text-xs"
                    >
                        <Trash2 className="w-3.5 h-3.5" /> {isConfirmed ? "Archive" : "Dismiss"}
                    </button>
                    {!isConfirmed && (
                        <button
                            type="button"
                            data-testid="lead-detail-confirm"
                            onClick={onConfirm}
                            className="btn-tactical text-xs"
                        >
                            <CalendarPlus className="w-3.5 h-3.5" /> Confirm & schedule
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function Block({ label, children }) {
    return (
        <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-2">{label}</div>
            {children}
        </div>
    );
}

function SignedWaiverModal({ req, onClose }) {
    const w = req.waiver || {};
    return (
        <div data-testid="signed-waiver-modal" className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto">
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-2xl my-8 max-h-[calc(100vh-4rem)] flex flex-col">
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
                    <h3 className="text-lg font-semibold inline-flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-brand-navy-300" /> Signed Participant Agreement
                    </h3>
                    <button type="button" onClick={onClose} className="text-zinc-300 hover:text-white" data-testid="signed-waiver-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="px-6 py-4 grid sm:grid-cols-2 gap-3 text-xs border-b border-zinc-800">
                    <Stat label="Signed by" value={w.signed_name || "—"} />
                    <Stat label="Relationship" value={w.relationship || "—"} />
                    <Stat label="Signed at" value={w.signed_at ? new Date(w.signed_at).toLocaleString() : "—"} />
                    <Stat label="Version" value={w.version || "—"} />
                    <Stat label="IP address" value={w.signed_ip || "—"} />
                    <Stat label="Source" value={w.source || "—"} />
                </div>
                <div className="px-6 py-4 flex-1 overflow-y-auto">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-2">Frozen waiver text (what they actually saw)</div>
                    <div className="bg-black border border-zinc-900 rounded-lg p-4 text-[11px] text-zinc-200 leading-relaxed whitespace-pre-wrap font-mono">
                        {w.text_snapshot || "— Waiver text snapshot was not captured for this signature."}
                    </div>
                    {w.signed_user_agent && (
                        <div className="mt-3 text-[10px] text-zinc-500 break-all">
                            User-agent: {w.signed_user_agent}
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 border-t border-zinc-800 flex justify-end">
                    <button type="button" onClick={onClose} className="btn-ghost-tactical">Close</button>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400">{label}</div>
            <div className="text-zinc-100">{value}</div>
        </div>
    );
}

function ConfirmSlotModal({ req, onClose, onDone }) {
    const [weekStart, setWeekStart] = useState(() => startOfWeekMon(new Date()));
    const [slots, setSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(true);
    const [chosenSlot, setChosenSlot] = useState(null);
    // Multi-athlete: a parent can book multiple kids into one assessment.
    // Default to ALL athletes on the request checked — that's what Sachin
    // expected ("schedule both kids in"). Coach can uncheck if only one's
    // coming. Stored as a Set for O(1) toggling.
    const [chosenAthleteIds, setChosenAthleteIds] = useState(
        () => new Set((req.athletes || []).map((a) => a.athlete_id)),
    );
    const [submitting, setSubmitting] = useState(false);
    const [activeDay, setActiveDay] = useState(null); // YYYY-MM-DD; null = auto-pick first

    const weekFrom = useMemo(() => isoDate(weekStart), [weekStart]);
    const weekTo = useMemo(() => isoDate(addDays(weekStart, 13)), [weekStart]);

    // Group slots by date so we can render day tabs
    const dayTabs = useMemo(() => {
        const groups = new Map();
        for (const s of slots) {
            if (!groups.has(s.date)) groups.set(s.date, []);
            groups.get(s.date).push(s);
        }
        const sorted = Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
        return sorted.map(([date, list]) => {
            const dt = new Date(`${date}T00:00:00`);
            return {
                key: date,
                dayShort: dt.toLocaleDateString(undefined, { weekday: "short" }),
                monthDay: dt.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
                count: list.length,
            };
        });
    }, [slots]);

    // Auto-pick the first day with open slots whenever the window/slot set changes
    const currentDayKey = activeDay || dayTabs[0]?.key || null;
    const filteredSlots = useMemo(
        () => slots.filter((s) => s.date === currentDayKey).sort((a, b) => a.start_time.localeCompare(b.start_time)),
        [slots, currentDayKey],
    );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoadingSlots(true);
            try {
                const r = await axios.get(`${API}/coach/schedule/slots`, {
                    params: { from_date: weekFrom, to_date: weekTo },
                    withCredentials: true,
                });
                if (cancelled) return;
                setSlots((r.data || []).filter((s) => (s.booking_count || 0) < (s.capacity || 1)));
            } catch (e) {
                toast.error("Couldn't load slots", { description: e?.response?.data?.detail });
            } finally {
                if (!cancelled) setLoadingSlots(false);
            }
        })();
        return () => { cancelled = true; };
    }, [weekFrom, weekTo]);

    const submit = async () => {
        const athleteIds = Array.from(chosenAthleteIds);
        if (!chosenSlot || athleteIds.length === 0) {
            toast.error("Pick at least one athlete and a slot");
            return;
        }
        // Slot capacity sanity check on the client so we don't round-trip a
        // doomed request. Server enforces this too.
        const open = (chosenSlot.capacity || 0) - (chosenSlot.booking_count || 0);
        if (athleteIds.length > open) {
            toast.error(`Only ${open} open seat${open === 1 ? "" : "s"} in that slot — uncheck ${athleteIds.length - open}`);
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(
                `${API}/coach/assessment-requests/${req.request_id}/confirm`,
                { slot_id: chosenSlot.slot_id, athlete_ids: athleteIds },
                { withCredentials: true },
            );
            toast.success(athleteIds.length > 1 ? `Confirmed for ${athleteIds.length} athletes — parent emailed` : "Confirmed — parent has been emailed");
            onDone();
        } catch (e) {
            toast.error("Couldn't confirm", { description: e?.response?.data?.detail });
        } finally {
            setSubmitting(false);
        }
    };

    const toggleAthlete = (athleteId) => {
        setChosenAthleteIds((prev) => {
            const next = new Set(prev);
            if (next.has(athleteId)) next.delete(athleteId);
            else next.add(athleteId);
            return next;
        });
    };

    return (
        <div data-testid="confirm-slot-modal" className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto">
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-2xl my-8">
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Confirm assessment slot</h3>
                    <button type="button" onClick={onClose} className="text-zinc-300 hover:text-white">×</button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="text-sm text-zinc-200">
                        Booking <strong className="text-white">{req.parent_name}</strong>
                    </div>
                    {req.athletes?.length >= 1 && (
                        <div>
                            <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-300 mb-2">
                                {req.athletes.length > 1 ? "Athletes" : "Athlete"}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {req.athletes.map((a) => {
                                    const checked = chosenAthleteIds.has(a.athlete_id);
                                    return (
                                        <label
                                            key={a.athlete_id}
                                            data-testid={`confirm-athlete-row-${a.athlete_id}`}
                                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                                                checked
                                                    ? "border-brand-red bg-brand-red/10"
                                                    : "border-zinc-800 bg-zinc-950 hover:border-zinc-600"
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                data-testid={`confirm-athlete-check-${a.athlete_id}`}
                                                checked={checked}
                                                onChange={() => toggleAthlete(a.athlete_id)}
                                                className="w-4 h-4 accent-brand-red"
                                            />
                                            <span className="text-sm text-white truncate">{a.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                            {chosenAthleteIds.size > 1 && (
                                <div
                                    data-testid="confirm-athletes-count"
                                    className="mt-2 text-[10px] tracking-[0.2em] uppercase text-zinc-400"
                                >
                                    Booking {chosenAthleteIds.size} athletes into the same slot
                                </div>
                            )}
                        </div>
                    )}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-300">Pick an open slot</span>
                            <div className="flex gap-2 text-xs">
                                <button type="button" onClick={() => { setWeekStart((d) => addDays(d, -7)); setActiveDay(null); }} className="text-zinc-200 hover:text-white px-2">‹ Prev</button>
                                <button type="button" onClick={() => { setWeekStart((d) => addDays(d, 7)); setActiveDay(null); }} className="text-zinc-200 hover:text-white px-2">Next ›</button>
                            </div>
                        </div>
                        {loadingSlots ? (
                            <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-brand-red" /></div>
                        ) : slots.length === 0 ? (
                            <p className="text-sm text-zinc-300 py-4">No open slots in this 2-week window. Check the next week or create a one-off slot.</p>
                        ) : (
                            <>
                                {/* Day tabs */}
                                <div data-testid="confirm-slot-day-tabs" className="flex gap-1 overflow-x-auto pb-2 mb-3 -mx-1 px-1 scrollbar-thin">
                                    {dayTabs.map((d) => {
                                        const isActive = d.key === currentDayKey;
                                        return (
                                            <button
                                                key={d.key}
                                                type="button"
                                                data-testid={`confirm-day-tab-${d.key}`}
                                                onClick={() => setActiveDay(d.key)}
                                                className={`flex-shrink-0 px-3 py-2 rounded-md text-center border transition-colors ${
                                                    isActive
                                                        ? "border-brand-red bg-brand-red/10 text-white"
                                                        : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-600"
                                                }`}
                                            >
                                                <div className="text-[10px] tracking-[0.2em] uppercase opacity-80">{d.dayShort}</div>
                                                <div className="text-sm font-display tracking-wider">{d.monthDay}</div>
                                                <div className="text-[10px] text-zinc-500 mt-0.5">{d.count} slot{d.count === 1 ? "" : "s"}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                                {/* Slots for active day */}
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
                                    {filteredSlots.map((s) => (
                                        <button
                                            key={s.slot_id}
                                            type="button"
                                            data-testid={`pick-slot-${s.slot_id}`}
                                            onClick={() => setChosenSlot(s)}
                                            className={`text-left rounded-lg border px-3 py-2 transition ${
                                                chosenSlot?.slot_id === s.slot_id
                                                    ? "border-brand-red bg-brand-red/10"
                                                    : "border-zinc-700 bg-zinc-900 hover:border-zinc-500"
                                            }`}
                                        >
                                            <div className="text-sm font-display tracking-wider text-white">{formatTimeDisplay(s.start_time)}</div>
                                            <div className="text-[10px] text-zinc-300 mt-0.5">{s.label || `${s.duration_minutes}min`} · {s.booking_count}/{s.capacity}</div>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="btn-ghost-tactical" disabled={submitting}>Cancel</button>
                        <button
                            type="button"
                            data-testid="confirm-submit"
                            onClick={submit}
                            disabled={submitting || !chosenSlot || chosenAthleteIds.size === 0}
                            className="btn-tactical disabled:opacity-50"
                        >
                            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                            Confirm
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}



function DroppedCard({ rec, onRecover, onDismiss }) {
    const name = `${rec.parent_first_name || ""} ${rec.parent_last_name || ""}`.trim() || "(no name)";
    const when = rec.created_at ? new Date(rec.created_at).toLocaleString() : "—";
    const reasonLabel = ({
        honeypot: "Honeypot field tripped",
        too_fast: `Submitted too fast (${rec.extra?.elapsed_ms ?? "?"}ms after opening)`,
        rate_limited: `Rate-limited (${rec.extra?.recent_count ?? "?"} submissions from this IP in the last hour)`,
    })[rec.reason] || rec.reason;
    return (
        <div data-testid={`dropped-card-${rec.dropped_id}`} className="border border-amber-700/50 bg-zinc-950 rounded-xl p-4 sm:p-5">
            <div className="flex items-start gap-3 mb-3">
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-amber-300 inline-flex items-center gap-1.5">
                        <AlertTriangle className="w-3 h-3" /> {reasonLabel}
                    </div>
                    <h3 className="text-xl font-bold text-white mt-1 truncate">{name}</h3>
                    <div className="text-[11px] text-zinc-400 mt-0.5">{when}</div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                    <button
                        type="button"
                        data-testid={`recover-${rec.dropped_id}`}
                        onClick={onRecover}
                        className="text-xs px-3 py-1.5 bg-brand-red text-white inline-flex items-center gap-1.5 hover:opacity-90"
                    >
                        <RotateCcw className="w-3 h-3" /> Recover as lead
                    </button>
                    <button
                        type="button"
                        data-testid={`bot-${rec.dropped_id}`}
                        onClick={onDismiss}
                        className="text-xs px-3 py-1.5 border border-zinc-800 text-zinc-300 hover:text-white hover:border-zinc-700 inline-flex items-center gap-1.5"
                    >
                        <Trash2 className="w-3 h-3" /> Mark as bot
                    </button>
                </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-2 text-xs text-zinc-200">
                <div className="inline-flex items-center gap-2 truncate">
                    <Mail className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                    <span className="truncate">{rec.email || "—"}</span>
                </div>
                <div className="inline-flex items-center gap-2 truncate">
                    <Phone className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                    <span className="truncate">{rec.phone || "—"}</span>
                </div>
                <div className="inline-flex items-center gap-2 truncate sm:col-span-2">
                    <ClipboardList className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                    <span className="truncate">Preferred time: {rec.preferred_time || "—"}</span>
                </div>
                {Array.isArray(rec.athletes) && rec.athletes.length > 0 && (
                    <div className="inline-flex items-start gap-2 sm:col-span-2">
                        <User className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-300">
                            Athletes: {rec.athletes.map((a) => `${a.first_name || ""} ${a.last_name || ""}`.trim()).filter(Boolean).join(", ") || "—"}
                        </span>
                    </div>
                )}
                {(rec.notes || rec.goals) && (
                    <div className="sm:col-span-2 text-zinc-400 mt-1">
                        {rec.notes && <div><span className="text-zinc-500">Notes:</span> {rec.notes}</div>}
                        {rec.goals && <div><span className="text-zinc-500">Goals:</span> {rec.goals}</div>}
                    </div>
                )}
                <div className="sm:col-span-2 text-[10px] text-zinc-500 mt-1 truncate">
                    IP: {rec.submitter_ip || "—"} · UA: {(rec.user_agent || "—").slice(0, 80)}
                </div>
            </div>
        </div>
    );
}
